import { useState } from 'react';

interface GameSession {
  id: string;
  name: string;
  numPlayers: 2 | 3 | 4;
  status: 'DRAFT' | 'SETUP' | 'ACTIVE' | 'COMPLETED';
  createdAt: string;
  startedAt?: string;
  endedAt?: string;
}

export interface SessionInfoCollapsibleProps {
  session: GameSession | null;
  activeVotes: any[];
  socketConnected: boolean;
  recentActions?: any[];
}

export function SessionInfoCollapsible({
  session,
  activeVotes,
  socketConnected,
  recentActions = [],
}: SessionInfoCollapsibleProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!session) {
    return null;
  }

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
      {/* Header (Always Visible) */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-gray-750 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-xl">{isExpanded ? '▼' : '▶'}</span>
          <h3 className="text-lg font-bold text-white">Info Sessione</h3>
          <span className="text-gray-400 text-sm">
            ({activeVotes.length} voti, {socketConnected ? 'connesso' : 'disconnesso'})
          </span>
        </div>
        <span className="text-gray-500 text-sm">
          {isExpanded ? 'Clicca per chiudere' : 'Clicca per espandere'}
        </span>
      </button>

      {/* Collapsible Content */}
      {isExpanded && (
        <div className="p-4 border-t border-gray-700 space-y-4">
          {/* Session Details */}
          <div>
            <h4 className="text-white font-semibold mb-2">Dettagli Sessione</h4>
            <div className="bg-gray-900 rounded p-3 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">ID:</span>
                <span className="text-white font-mono">{session.id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Nome:</span>
                <span className="text-white">{session.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Giocatori:</span>
                <span className="text-white">{session.numPlayers}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Stato:</span>
                <span className="text-white font-semibold">{session.status}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Creata:</span>
                <span className="text-white">{new Date(session.createdAt).toLocaleString()}</span>
              </div>
              {session.startedAt && (
                <div className="flex justify-between">
                  <span className="text-gray-400">Avviata:</span>
                  <span className="text-white">{new Date(session.startedAt).toLocaleString()}</span>
                </div>
              )}
            </div>
          </div>

          {/* Active Votes Details */}
          {activeVotes.length > 0 && (
            <div>
              <h4 className="text-white font-semibold mb-2">Dettagli Votazioni</h4>
              <div className="bg-gray-900 rounded p-3 space-y-2">
                {activeVotes.map((vote: any) => (
                  <div key={vote.id} className="text-sm border-b border-gray-800 last:border-0 pb-2 last:pb-0">
                    <div className="flex justify-between mb-1">
                      <span className="text-white font-semibold">Squadra {vote.teamColor}</span>
                      <span className={`text-xs px-2 py-0.5 rounded ${vote.isActive ? 'bg-green-900 text-green-300' : 'bg-gray-700 text-gray-400'}`}>
                        {vote.isActive ? 'Attiva' : 'Chiusa'}
                      </span>
                    </div>
                    <div className="text-gray-400 text-xs">
                      {vote.submissions?.length || 0} voti
                      {vote.options && ` • ${vote.options.length} opzioni`}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Connection Status */}
          <div>
            <h4 className="text-white font-semibold mb-2">Stato Connessione</h4>
            <div className="bg-gray-900 rounded p-3">
              <div className="flex items-center gap-2">
                <span className={`w-3 h-3 rounded-full ${socketConnected ? 'bg-green-500' : 'bg-red-500'}`}></span>
                <span className="text-white text-sm">
                  Socket.IO: {socketConnected ? 'Connesso' : 'Disconnesso'}
                </span>
              </div>
            </div>
          </div>

          {/* Recent Actions (Future) */}
          {recentActions.length > 0 && (
            <div>
              <h4 className="text-white font-semibold mb-2">Azioni Recenti</h4>
              <div className="bg-gray-900 rounded p-3 space-y-1 max-h-40 overflow-y-auto">
                {recentActions.map((action: any, idx: number) => (
                  <div key={idx} className="text-xs text-gray-400">
                    [{action.timestamp}] {action.description}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
